import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

declare var YT: any;

/** How long to wait for the YouTube player to become ready before showing the fallback link. */
const PLAYER_READY_TIMEOUT_MS = 8000;

@Component({
  selector: 'app-video-dialog',
  templateUrl: './video-dialog.component.html',
  styleUrls: ['./video-dialog.component.scss']
})
export class VideoDialogComponent implements OnInit, OnDestroy {
  player!: any; // YouTube Player instance
  videoId: string = '';
  /** True once the embedded player could not be loaded; the template then shows a link to YouTube instead. */
  loadFailed = false;
  /** Direct link to the video on YouTube, used as the fallback when the embed fails. */
  watchUrl = '';

  private readyTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    public dialogRef: MatDialogRef<VideoDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { videoUrl: string }
  ) { }

  ngOnInit(): void {
    this.videoId = this.extractVideoId(this.data.videoUrl);
    this.watchUrl = this.videoId
      ? `https://www.youtube.com/watch?v=${this.videoId}`
      : this.data.videoUrl;

    if (!this.videoId) {
      this.showFallback('Could not extract a YouTube video id from the URL');
      return;
    }

    this.startReadyTimer();
    this.loadYouTubePlayer();
  }

  loadYouTubePlayer(): void {
    if (typeof YT === 'undefined' || !YT.Player) {
      (window as any).onYouTubeIframeAPIReady = () => this.initializePlayer();

      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      script.async = true;
      script.defer = true;
      // Fires when the script is blocked (CSP, network, Google bot-check redirect, ad blocker).
      script.onerror = () => this.showFallback('YouTube iframe API script failed to load');
      document.body.appendChild(script);
    } else {
      this.initializePlayer();
    }
  }

  initializePlayer(): void {
    if (this.loadFailed) {
      return;
    }

    this.player = new YT.Player('youtubePlayer', {
      videoId: this.videoId,
      playerVars: {
        autoplay: 1,
        controls: 1,
        rel: 0,
        showinfo: 0,
        mute: 1
      },
      events: {
        'onReady': () => this.clearReadyTimer(),
        'onStateChange': (event: any) => this.onPlayerStateChange(event),
        // Error codes: 2 invalid id, 5 HTML5 error, 100 not found/private, 101/150 embedding disabled.
        'onError': (event: any) => this.showFallback(`YouTube player error ${event?.data}`)
      },
      height: '100%',
      width: '100%',
    });
  }

  onPlayerStateChange(event: any): void {
    if (event.data === YT.PlayerState.ENDED) {
      this.dialogRef.close();
    }
  }

  extractVideoId(url: string): string {
    const regExp = /(?:youtube\.com\/(?:[^\/]+\/[^\/]+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url.match(regExp);
    return match && match[1] ? match[1] : '';
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.clearReadyTimer();
    if (this.player) {
      this.player.destroy();
    }
  }

  private startReadyTimer(): void {
    this.readyTimer = setTimeout(
      () => this.showFallback(`YouTube player did not become ready within ${PLAYER_READY_TIMEOUT_MS}ms`),
      PLAYER_READY_TIMEOUT_MS
    );
  }

  private clearReadyTimer(): void {
    if (this.readyTimer) {
      clearTimeout(this.readyTimer);
      this.readyTimer = null;
    }
  }

  private showFallback(reason: string): void {
    if (this.loadFailed) {
      return;
    }
    console.warn(`[VideoDialog] Falling back to external YouTube link: ${reason}`);
    this.clearReadyTimer();
    this.loadFailed = true;
    if (this.player) {
      try {
        this.player.destroy();
      } catch {
        // Player may be in a broken state; nothing more to do.
      }
      this.player = null;
    }
  }
}
